/**
 * Bot rules: the standing instructions composed into every prompt.
 *
 * The table renders in PROMPT ORDER — position asc, slug tiebreak — the exact
 * sequence the composer emits, so there is no second ordering to reason
 * about. Editing follows the house invariant: saving creates an inactive
 * version, activating is a second click; the active toggle is
 * activate/deactivate on the current version.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { ApiError, apiGet, apiPost } from '../api/client';
import { useBrands } from '../api/admin';
import { Badge, Button, Card, Table, Td, Th } from '../components/ui/primitives';
import { cn } from '../lib/cn';

interface RuleRow {
  id: string;
  brandId: string | null;
  slug: string;
  title: string;
  rule: string;
  channels: string[];
  position: number;
  version: number;
  active: boolean;
  scope: 'shared' | 'brand';
  hasBrandRows: boolean;
}

interface RulesResponse {
  rules: RuleRow[];
  /** Slugs that exist but have no active version in this scope. */
  inactiveSlugs: string[];
}

const CHANNEL_OPTIONS = ['email', 'chat', 'phone-call', 'web'];

export function BotRulesPage() {
  const brands = useBrands();
  const [brandId, setBrandId] = useState('');
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const scopeQuery = brandId ? `?brandId=${brandId}` : '';
  const list = useQuery({
    queryKey: ['bot-rules', brandId || 'shared'],
    queryFn: () => apiGet<RulesResponse>(`/api/bot-rules${scopeQuery}`),
  });

  if (list.isLoading) {
    return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }

  const rules = list.data?.rules ?? [];

  return (
    <div className="mx-auto max-w-[1100px] px-5 py-5">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="rules-brand" className="eyebrow mb-1 block text-ink-500">
            Rules in force for
          </label>
          <select
            id="rules-brand"
            value={brandId}
            onChange={(event) => {
              setBrandId(event.target.value);
              setOpenSlug(null);
            }}
            className="rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink-900"
          >
            <option value="">Shared (every brand inherits)</option>
            {(brands.data ?? []).map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>
        <Button size="sm" onClick={() => setCreating((open) => !open)}>
          <Plus size={13} strokeWidth={2} aria-hidden />
          New rule
        </Button>
        <p className="text-2xs text-ink-400">
          The order below is the order the prompt composes them — position first, slug as the
          tiebreak.
        </p>
      </div>

      {creating ? (
        <Card className="mb-4">
          <RuleEditor
            brandId={brandId || null}
            initial={null}
            onDone={() => setCreating(false)}
          />
        </Card>
      ) : null}

      {rules.length === 0 ? (
        <Card>
          <p className="px-4 py-10 text-center text-sm text-ink-400">
            No rules are active in this scope.
          </p>
        </Card>
      ) : (
        <Card>
          <Table>
            <thead>
              <tr>
                <Th className="w-16">Order</Th>
                <Th>Rule</Th>
                <Th>Channels</Th>
                <Th>Scope</Th>
                <Th>Version</Th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <RuleTableRow
                  key={rule.id}
                  rule={rule}
                  brandId={brandId || null}
                  open={openSlug === rule.slug}
                  onToggle={() => setOpenSlug(openSlug === rule.slug ? null : rule.slug)}
                />
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {(list.data?.inactiveSlugs.length ?? 0) > 0 ? (
        <p className="mt-3 text-xs text-ink-400">
          Deactivated in this scope: {list.data?.inactiveSlugs.join(', ')} — reactivate from the
          rule&rsquo;s version history.
        </p>
      ) : null}
    </div>
  );
}

function RuleTableRow({
  rule,
  brandId,
  open,
  onToggle,
}: {
  rule: RuleRow;
  brandId: string | null;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className="align-top">
        <Td className="tabular text-xs text-ink-400">{rule.position}</Td>
        <Td>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="text-left font-medium text-ink-900 hover:text-accent-700"
          >
            {rule.title}
          </button>
          <div className="mt-0.5 line-clamp-2 max-w-[560px] text-xs text-ink-500">{rule.rule}</div>
        </Td>
        <Td>
          {rule.channels.length === 0 ? (
            <span className="text-2xs text-ink-400">every channel</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {rule.channels.map((channel) => (
                <Badge key={channel}>{channel}</Badge>
              ))}
            </div>
          )}
        </Td>
        <Td>
          <Badge tone={rule.scope === 'brand' ? 'accent' : 'neutral'}>
            {rule.scope === 'brand' ? 'override' : 'shared'}
          </Badge>
        </Td>
        <Td className="tabular text-xs text-ink-500">v{rule.version}</Td>
      </tr>
      {open ? (
        <tr>
          <Td colSpan={5} className="bg-surface-sunken">
            <RuleEditor brandId={brandId} initial={rule} onDone={onToggle} />
          </Td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * Edit (or create) a rule. Saving makes a new inactive version; the buttons
 * below say exactly which act publishes.
 */
function RuleEditor({
  brandId,
  initial,
  onDone,
}: {
  brandId: string | null;
  initial: RuleRow | null;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [rule, setRule] = useState(initial?.rule ?? '');
  const [channels, setChannels] = useState<string[]>(initial?.channels ?? []);
  const [position, setPosition] = useState(initial?.position ?? 0);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['bot-rules'] });

  const save = useMutation({
    mutationFn: () =>
      apiPost<{ id: string; version: number }>('/api/bot-rules', {
        slug: slug.trim(),
        brandId,
        title: title.trim(),
        rule: rule.trim(),
        channels,
        position,
      }),
    onSuccess: async (created) => {
      setSavedId(created.id);
      setOutcome(`Saved as v${created.version} — inactive until you activate it.`);
      setProblem(null);
      await refresh();
    },
    onError: (error: unknown) =>
      setProblem(error instanceof ApiError ? error.message : 'Could not save.'),
  });

  const activate = useMutation({
    mutationFn: (id: string) => apiPost(`/api/bot-rules/${id}/activate`),
    onSuccess: async () => {
      setOutcome('Activated — the rule now composes into prompts.');
      setProblem(null);
      await refresh();
      onDone();
    },
    onError: (error: unknown) =>
      setProblem(error instanceof ApiError ? error.message : 'Could not activate.'),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => apiPost(`/api/bot-rules/${id}/deactivate`),
    onSuccess: async () => {
      setOutcome('Deactivated — the rule no longer composes.');
      setProblem(null);
      await refresh();
      onDone();
    },
    onError: (error: unknown) =>
      setProblem(error instanceof ApiError ? error.message : 'Could not deactivate.'),
  });

  return (
    <div className="grid gap-3 py-2 lg:grid-cols-[1.5fr_1fr]">
      <div>
        <div className="mb-2 grid grid-cols-[1fr_1fr_90px] gap-2">
          <label className="block">
            <span className="eyebrow mb-1 block text-ink-500">Slug</span>
            <input
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              disabled={initial !== null}
              placeholder="never-promise-delivery-dates"
              className="w-full rounded border border-line bg-surface px-2 py-1.5 font-mono text-xs text-ink-900 disabled:text-ink-400"
            />
          </label>
          <label className="block">
            <span className="eyebrow mb-1 block text-ink-500">Title</span>
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="w-full rounded border border-line bg-surface px-2 py-1.5 text-xs text-ink-900"
            />
          </label>
          <label className="block">
            <span className="eyebrow mb-1 block text-ink-500">Position</span>
            <input
              type="number"
              value={position}
              onChange={(event) => setPosition(Number(event.target.value))}
              className="tabular w-full rounded border border-line bg-surface px-2 py-1.5 text-xs text-ink-900"
            />
          </label>
        </div>
        <textarea
          value={rule}
          onChange={(event) => setRule(event.target.value)}
          rows={4}
          placeholder="The rule, as an instruction to the agent. {{brand.name}} and {{brand.supportEmail}} render per brand."
          aria-label="Rule text"
          className="w-full rounded border border-line bg-surface px-2.5 py-2 text-sm text-ink-700"
        />
      </div>

      <div>
        <div className="eyebrow mb-1.5 text-ink-500">Channels</div>
        <div className="mb-3 flex flex-wrap gap-1.5">
          {CHANNEL_OPTIONS.map((channel) => {
            const on = channels.includes(channel);
            return (
              <button
                key={channel}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  setChannels((current) =>
                    on ? current.filter((c) => c !== channel) : [...current, channel],
                  )
                }
                className={cn(
                  'rounded-sm px-2 py-0.5 text-2xs font-semibold transition-colors duration-fast',
                  on
                    ? 'bg-accent-50 text-accent-700 shadow-hairline'
                    : 'bg-surface text-ink-400 shadow-hairline hover:text-ink-700',
                )}
              >
                {channel}
              </button>
            );
          })}
        </div>
        <p className="mb-3 text-2xs text-ink-400">No channels selected = applies everywhere.</p>

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="primary"
            disabled={save.isPending || !slug.trim() || !title.trim() || !rule.trim()}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save as new version'}
          </Button>
          {savedId ? (
            <Button size="sm" onClick={() => activate.mutate(savedId)} disabled={activate.isPending}>
              Activate the saved version
            </Button>
          ) : null}
          {initial?.active ? (
            <Button
              size="sm"
              variant="danger"
              onClick={() => deactivate.mutate(initial.id)}
              disabled={deactivate.isPending}
            >
              Deactivate
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={onDone}>
            Close
          </Button>
        </div>

        {outcome ? <p className="mt-2 text-xs text-ok-700">{outcome}</p> : null}
        {problem ? <p className="mt-2 text-xs text-stop-700">{problem}</p> : null}
      </div>
    </div>
  );
}
